import React, { memo, useEffect, useState, useMemo } from "react";
import classNames from "classnames";
import { Card, CardBody, CardHeader } from "reactstrap";
import { useTranslation } from "react-i18next";
import {
  getTheme2SidebarNavFontSizePxFromStorage,
  THEME2_SIDEBAR_NAV_TYPOGRAPHY_CHANGED_EVENT,
} from "utils/sidebarShellTheme";
import { getVehicleStatusDisplay } from "utils/vehicleStatusDisplay";
import "./InventoryInfoCardsTheme2.css";

const NEUTRAL_ACCENT_BORDER = "rgb(194, 218, 231)";
const SURFACE_LIGHT = "#ffffff";
const SURFACE_DARK = "#272A3D";
const TEXT_LIGHT = "#3c4858";
const TEXT_DARK = "#ffffff";

function getCardSurfaceStyle(isLightTheme, accentBorderColor) {
  return {
    width: "100%",
    minHeight: 150,
    background: isLightTheme ? SURFACE_LIGHT : SURFACE_DARK,
    "--iic2-accent-border": accentBorderColor,
    boxShadow: "none",
  };
}

const InventoryInfoCardsTheme2 = memo(
  ({
    data,
    formatUSD,
    vehicleAge,
    daysInInventory,
    isLightTheme = false,
    onImagesCountClick,
    imagesCountLinkColor,
  }) => {
    const { t } = useTranslation();
    const [theme2FontSizePx, setTheme2FontSizePx] = useState(() =>
      getTheme2SidebarNavFontSizePxFromStorage()
    );

    const textColor = isLightTheme ? TEXT_LIGHT : TEXT_DARK;
    const vehicleStatusDisplay = getVehicleStatusDisplay(
      data?.inventory_vehicle?.vehicle_status
    );
    const vehicleBorderColor =
      vehicleStatusDisplay.color || NEUTRAL_ACCENT_BORDER;

    useEffect(() => {
      const syncFontSize = () => {
        setTheme2FontSizePx(getTheme2SidebarNavFontSizePxFromStorage());
      };

      window.addEventListener(THEME2_SIDEBAR_NAV_TYPOGRAPHY_CHANGED_EVENT, syncFontSize);
      window.addEventListener("storage", syncFontSize);
      return () => {
        window.removeEventListener(THEME2_SIDEBAR_NAV_TYPOGRAPHY_CHANGED_EVENT, syncFontSize);
        window.removeEventListener("storage", syncFontSize);
      };
    }, []);

    const wrapperStyle = useMemo(
      () => ({
        gap: 12,
        alignItems: "stretch",
        "--iic2-font-size": `${theme2FontSizePx}px`,
        "--iic2-heading-font-size": `${theme2FontSizePx + 2}px`,
        "--iic2-jd-value-font-size": `${theme2FontSizePx + 4}px`,
      }),
      [theme2FontSizePx]
    );

    const valueEllipsisStyle = useMemo(
      () => ({
        color: textColor,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        maxWidth: "200px",
      }),
      [textColor]
    );

    const jdValueEllipsisStyle = useMemo(
      () => ({
        fontSize: "var(--iic2-jd-value-font-size)",
        color: textColor,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        maxWidth: "150px",
      }),
      [textColor]
    );

    return (
      <div
        className={classNames(
          "inventory-info-cards-theme2 d-flex flex-column flex-lg-row",
          isLightTheme
            ? "inventory-info-cards-theme2--light"
            : "inventory-info-cards-theme2--dark"
        )}
        style={wrapperStyle}
      >
        <Card
          className="iic2-card"
          style={getCardSurfaceStyle(isLightTheme, vehicleBorderColor)}
        >
          <CardHeader className="pt-1">
            <h4 className="mb-0">{t("inventoryInfoCards.inventoryDetail")}</h4>
          </CardHeader>
          <CardBody className="iic2-card-body" style={{ paddingTop: 5 }}>
            <div className="iic2-card-body-main">
              <div className="iic2-row">
                <span className="iic2-label">{t("inventoryInfoCards.status")}:</span>
                <span
                  className="iic2-value"
                  style={{
                    color: vehicleStatusDisplay.color,
                    fontWeight: vehicleStatusDisplay.fontWeight,
                  }}
                >
                  {vehicleStatusDisplay.label}
                </span>
              </div>
              <div className="iic2-row">
                <span className="iic2-label">{t("inventoryInfoCards.description")}:</span>
                <span className="iic2-value" style={valueEllipsisStyle}>
                  {data?.inventory_vehicle?.year + " "}
                  {data?.inventory_vehicle?.make + " "}
                  {data?.inventory_vehicle?.model}
                </span>
              </div>
              <div className="iic2-row">
                <span className="iic2-label">{t("inventoryInfoCards.vin")}:</span>
                <span className="iic2-value" style={valueEllipsisStyle}>
                  {data?.inventory_vehicle?.vin || "-"}
                </span>
              </div>
              <div className="iic2-row">
                <span className="iic2-label">{t("inventoryInfoCards.stockNumber")}:</span>
                <span className="iic2-value" style={{ color: textColor }}>
                  {data?.stock_number || "-"}
                </span>
              </div>
              <div className="iic2-row">
                <span className="iic2-label">{t("inventoryInfoCards.bodyTrim")}:</span>
                <span className="iic2-value" style={valueEllipsisStyle}>
                  {data?.inventory_vehicle?.trim || "-"}
                </span>
              </div>
              <div className="iic2-row">
                <span className="iic2-label">{t("inventoryInfoCards.bodyStyle")}:</span>
                <span className="iic2-value" style={valueEllipsisStyle}>
                  {data?.inventory_vehicle?.body_style || "-"}
                </span>
              </div>
              <div className="iic2-row">
                <span className="iic2-label">{t("inventoryInfoCards.type")}:</span>
                <span className="iic2-value" style={{ color: textColor }}>
                  {data?.inventory_vehicle?.type?.toLocaleUpperCase() || "-"}
                </span>
              </div>
              <div className="iic2-row">
                <span className="iic2-label">{t("inventoryInfoCards.color")}:</span>
                <span className="iic2-value" style={valueEllipsisStyle}>
                  {data?.inventory_vehicle?.exterior_color || "-"}
                </span>
              </div>
              <div className="iic2-row">
                <span className="iic2-label">{t("inventoryInfoCards.imagesAvailable")}:</span>
                {onImagesCountClick ? (
                  <button
                    type="button"
                    className="iic2-value iic2-value-link"
                    style={{ color: imagesCountLinkColor || textColor }}
                    onClick={onImagesCountClick}
                  >
                    {data?.images_count || 0}
                  </button>
                ) : (
                  <span className="iic2-value" style={{ color: textColor }}>
                    {data?.images_count || 0}
                  </span>
                )}
              </div>
              <div className="iic2-row">
                <span className="iic2-label">{t("inventoryInfoCards.actualMileage")}:</span>
                <span className="iic2-value" style={{ color: textColor }}>
                  {data?.inventory_vehicle?.actual_mileage || "-"}
                </span>
              </div>
              <div className="iic2-row">
                <span className="iic2-label">{t("inventoryInfoCards.condition")}:</span>
                <span className="iic2-value" style={valueEllipsisStyle}>
                  {data?.inventory_vehicle?.condition || "-"}
                </span>
              </div>
              <div className="iic2-row">
                <span className="iic2-label">{t("inventoryInfoCards.vehicleAge")}:</span>
                <span className="iic2-value" style={{ color: textColor }}>
                  {vehicleAge !== undefined
                    ? `${vehicleAge} ${t("inventoryInfoCards.years")}`
                    : "-"}
                </span>
              </div>
            </div>
          </CardBody>
        </Card>

        <Card
          className="iic2-card"
          style={getCardSurfaceStyle(isLightTheme, NEUTRAL_ACCENT_BORDER)}
        >
          <CardHeader className="pt-1">
            <h4 className="mb-0">{t("inventoryInfoCards.pricingInformation")}</h4>
          </CardHeader>
          <CardBody className="iic2-card-body" style={{ paddingTop: 5 }}>
            <div className="iic2-card-body-main">
              <div className="iic2-row">
                <span className="iic2-label">{t("inventoryInfoCards.askingPrice")}:</span>
                <span className="iic2-value" style={{ color: textColor }}>
                  {formatUSD(data?.inventory_pricing?.normal_asking_price)}
                </span>
              </div>
              <div className="iic2-row">
                <span className="iic2-label">{t("inventoryInfoCards.askingDown")}:</span>
                <span className="iic2-value" style={{ color: textColor }}>
                  {formatUSD(data?.inventory_pricing?.asking_down)}
                </span>
              </div>
              <div className="iic2-row">
                <span className="iic2-label">{t("inventoryInfoCards.cost")}:</span>
                <span className="iic2-value" style={{ color: textColor }}>
                  {formatUSD(data?.inventory_pricing?.vehicle_cost)}
                </span>
              </div>
              <div className="iic2-row">
                <span className="iic2-label">{t("inventoryInfoCards.vehicleExpenses")}:</span>
                <span className="iic2-value" style={{ color: textColor }}>
                  {formatUSD(data?.vehicle_expenses)}
                </span>
              </div>
              <div className="iic2-row">
                <span className="iic2-label">{t("inventoryInfoCards.floorPlanExpense")}:</span>
                <span className="iic2-value" style={{ color: textColor }}>
                  {formatUSD(data?.floor_plan_expense)}
                </span>
              </div>
              <div className="iic2-row">
                <span className="iic2-label">{t("inventoryInfoCards.totalCost")}:</span>
                <span className="iic2-value" style={{ color: textColor }}>
                  {formatUSD(data?.inventory_pricing?.total_cost_in_car)}
                </span>
              </div>
              <div className="iic2-row">
                <span className="iic2-label">{t("inventoryInfoCards.buyersGuide")}:</span>
                <span className="iic2-value" style={valueEllipsisStyle}>
                  {data?.inventory_pricing?.buyers_guide_json?.guide_type || "-"}
                </span>
              </div>
              <div className="iic2-row">
                <span className="iic2-label">{t("inventoryInfoCards.maxTerms")}:</span>
                <span className="iic2-value" style={{ color: textColor }}>
                  {data?.max_terms || "-"}
                </span>
              </div>
              <div className="iic2-row">
                <span className="iic2-label">{t("inventoryInfoCards.repairOrders")}:</span>
                <span className="iic2-value" style={{ color: textColor }}>
                  {data?.repair_orders_count || "-"}
                </span>
              </div>
              <div className="iic2-row">
                <span className="iic2-label">{t("inventoryInfoCards.repairCost")}:</span>
                <span className="iic2-value" style={{ color: textColor }}>
                  {formatUSD(data?.repair_cost)}
                </span>
              </div>
            </div>
          </CardBody>
        </Card>

        <Card
          className="iic2-card"
          style={getCardSurfaceStyle(isLightTheme, NEUTRAL_ACCENT_BORDER)}
        >
          <CardHeader className="pt-1">
            <h4 className="mb-0">{t("inventoryInfoCards.locationInformation")}</h4>
          </CardHeader>
          <CardBody className="iic2-card-body" style={{ paddingTop: 5 }}>
            <div className="iic2-card-body-main">
              <div className="iic2-row">
                <span className="iic2-label">{t("inventoryInfoCards.location")}:</span>
                <span className="iic2-value" style={valueEllipsisStyle}>
                  {data?.location?.name || "-"}
                </span>
              </div>
              <div className="iic2-row">
                <span className="iic2-label">{t("inventoryInfoCards.altLocation")}:</span>
                <span className="iic2-value" style={valueEllipsisStyle}>
                  {data?.inventory_vehicle?.alt_location?.name || "-"}
                </span>
              </div>
              <div className="iic2-row">
                <span className="iic2-label">{t("inventoryInfoCards.inInventory")}:</span>
                <span className="iic2-value" style={{ color: textColor }}>
                  {data?.days_at_location === null || data?.days_at_location === undefined
                    ? "-"
                    : `${data?.days_at_location} ${t("inventoryInfoCards.days")}`}
                </span>
              </div>
              <div className="iic2-row">
                <span className="iic2-label">{t("inventoryInfoCards.daysAtLocation")}:</span>
                <span className="iic2-value" style={{ color: textColor }}>
                  {data?.days_in_inventory === null || data?.days_in_inventory === undefined
                    ? "-"
                    : `${data?.days_in_inventory} ${t("inventoryInfoCards.days")}`}
                </span>
              </div>
              <div className="iic2-row">
                <span className="iic2-label">{t("inventoryInfoCards.bookkeepingCo")}:</span>
                <span className="iic2-value" style={valueEllipsisStyle}>
                  {data?.location?.sales_location?.bookkeeping_company?.name || "-"}
                </span>
              </div>
            </div>
          </CardBody>
        </Card>

        <Card
          className="iic2-card"
          style={getCardSurfaceStyle(isLightTheme, NEUTRAL_ACCENT_BORDER)}
        >
          <CardHeader className="pt-1">
            <h4 className="mb-0">{t("inventoryInfoCards.jdPowerInformation")}</h4>
          </CardHeader>
          <CardBody className="iic2-card-body" style={{ paddingTop: 5 }}>
            <div className="iic2-card-body-main d-flex align-items-start">
              <div className="w-100">
                <div className="d-flex flex-column flex-md-row justify-content-between">
                  <div
                    className="mb-3 mb-md-0"
                    style={{ minWidth: 0, flexGrow: 1, paddingRight: 10 }}
                  >
                    <div className="iic2-label">
                      J.D. Power:
                      <br />
                      {t("inventoryInfoCards.retail")}
                    </div>
                    <div className="iic2-value" style={jdValueEllipsisStyle}>
                      {formatUSD(data?.inventory_pricing?.jd_power_json?.retail?.adj_total)}
                    </div>
                  </div>
                  <div style={{ minWidth: 0, flexShrink: 0 }}>
                    <div className="iic2-jd-wholesale-label">
                      {t("inventoryInfoCards.wholesale")}
                    </div>
                    <div className="iic2-value" style={jdValueEllipsisStyle}>
                      {formatUSD(
                        data?.inventory_pricing?.jd_power_json?.trade_in?.adj_total
                      ) || t("inventoryInfoCards.na")}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </CardBody>
        </Card>
      </div>
    );
  },
  (prevProps, nextProps) =>
    prevProps.data === nextProps.data &&
    prevProps.vehicleAge === nextProps.vehicleAge &&
    prevProps.daysInInventory === nextProps.daysInInventory &&
    prevProps.isLightTheme === nextProps.isLightTheme &&
    prevProps.onImagesCountClick === nextProps.onImagesCountClick &&
    prevProps.imagesCountLinkColor === nextProps.imagesCountLinkColor
);

InventoryInfoCardsTheme2.displayName = "InventoryInfoCardsTheme2";

export default InventoryInfoCardsTheme2;
